import type { RunnerCreated, RunnerDto } from '@fos/shared/contracts/agents';
import { normalizeRepoUrl } from '@fos/shared/contracts/agents';
import { TOKEN_MESSAGES } from '@fos/shared/contracts/tokens';
import {
  CLI_AUTH_STATUS_LABELS,
  RUNNER_STATUS_LABELS,
  RUNNER_STATUS_TONES,
  runnerSetupSteps,
} from '@fos/shared/labels';
import { Copy, Laptop, MoreHorizontal, Plus } from 'lucide-react';
import { type FormEvent, useEffect, useState } from 'react';
import { errorMessage, validationIssues } from '../../../lib/api';
import { formatDate, formatRelativePast } from '../../../lib/format';
import { trpc } from '../../../lib/trpc';
import { useAccess } from '../../../lib/useAccess';
import {
  Button,
  ContextBanner,
  Dialog,
  EmptyState,
  ErrorState,
  Field,
  FormMessage,
  IconButton,
  Input,
  Label,
  ListSkeleton,
  Menu,
  MenuContent,
  MenuItem,
  MenuTrigger,
  Mono,
  Section,
  Select,
  Textarea,
  toast,
} from '../../../ui';
import { StatusPill } from '../../../ui/StatusPill';
import { TOUCH_BUTTON, TOUCH_ICON } from '../../mobile';

/**
 * Agenci → Komputery: runnery osób (status online / offline z ostatniego kontaktu, wersja, Claude Code, repozytoria),
 * „Dodaj komputer” (token pokazany raz + instrukcja uruchomienia), zmiana nazwy, wyłączenie, nowy token. Token tworzy
 * wyłącznie osoba w aplikacji (step-up); serwer nigdy nie przechowuje logowania do Claude.
 */

async function copyText(text: string, done: string) {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(done);
  } catch {
    toast.error('Nie udało się skopiować. Zaznacz tekst i skopiuj ręcznie.');
  }
}

function serverUrl(): string {
  return typeof window === 'undefined' ? 'http://localhost:3001' : window.location.origin;
}

export function RunnersSection() {
  const access = useAccess();
  const runners = trpc.agents.listRunners.useQuery({});
  const [adding, setAdding] = useState(false);
  const [created, setCreated] = useState<RunnerCreated | null>(null);
  const canEdit = access.can('agents', 'edit');

  return (
    <Section aria-labelledby="komputery" className="flex flex-col gap-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 id="komputery" className="text-body-s font-semibold text-fg">
            Komputery z runnerem
          </h2>
          <p className="mt-1 max-w-[560px] text-body-s text-fg-muted">
            Runner odbiera Twoje zlecenia „Zleć Claude” i uruchamia Claude Code zalogowane na tym komputerze — tylko dla
            repozytoriów z jego listy.
          </p>
        </div>
        {canEdit && (
          <Button variant="primary" icon={Plus} onClick={() => setAdding(true)} className={TOUCH_BUTTON}>
            Dodaj komputer
          </Button>
        )}
      </div>
      {runners.isLoading ? (
        <ListSkeleton rows={2} />
      ) : runners.isError ? (
        <ErrorState compact onRetry={() => void runners.refetch()} retrying={runners.isFetching} />
      ) : runners.data && runners.data.length > 0 ? (
        <ul className="border-t border-line">
          {runners.data.map((r) => (
            <RunnerRow key={r.id} runner={r} canEdit={canEdit} onRotated={setCreated} />
          ))}
        </ul>
      ) : (
        <EmptyState
          icon={Laptop}
          title="Brak komputerów z runnerem"
          description="Dodaj komputer, żeby „Zleć Claude” mogło pracować w repozytorium na Twoim laptopie."
        />
      )}
      <AddRunnerDialog
        open={adding}
        onOpenChange={setAdding}
        onCreated={(c) => {
          setAdding(false);
          setCreated(c);
        }}
      />
      <RunnerTokenDialog created={created} onClose={() => setCreated(null)} />
    </Section>
  );
}

function RunnerRow({
  runner,
  canEdit,
  onRotated,
}: {
  runner: RunnerDto;
  canEdit: boolean;
  onRotated: (c: RunnerCreated) => void;
}) {
  const utils = trpc.useUtils();
  const [renaming, setRenaming] = useState(false);
  const update = trpc.agents.updateRunner.useMutation({
    onSuccess: async (r) => {
      toast.success(
        r.status !== 'disabled'
          ? 'Zapisano'
          : runner.activeRuns > 0
            ? 'Runner wyłączony, token cofnięty, trwające przebiegi przerwane'
            : 'Runner wyłączony, token cofnięty',
      );
      await utils.agents.invalidate();
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  const rotate = trpc.agents.rotateRunnerToken.useMutation({
    onSuccess: async (c) => {
      await utils.agents.invalidate();
      onRotated(c);
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  const cli = runner.capabilities?.claudeCli;
  const meta = [
    runner.user.name,
    runner.version ? `runner ${runner.version}` : null,
    cli ? `Claude Code: ${cli.available ? CLI_AUTH_STATUS_LABELS[cli.authStatus] : 'niedostępne'}` : null,
    runner.lastSeenAt ? `kontakt ${formatRelativePast(runner.lastSeenAt)}` : 'jeszcze się nie zgłosił',
  ].filter(Boolean);
  return (
    <li className="flex flex-col gap-2 border-b border-line py-4 md:flex-row md:items-start md:gap-4">
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-body-s font-medium text-fg">{runner.name}</span>
          <StatusPill tone={RUNNER_STATUS_TONES[runner.status]}>{RUNNER_STATUS_LABELS[runner.status]}</StatusPill>
          {runner.activeRuns > 0 && <span className="text-caption text-accent">pracuje ({runner.activeRuns})</span>}
        </div>
        <p className="text-caption text-fg-muted">{meta.join(' · ')}</p>
        {runner.repoAllowlist.length > 0 ? (
          <ul className="mt-1 flex flex-col gap-0.5" aria-label="Repozytoria">
            {runner.repoAllowlist.map((repo) => (
              <li key={repo}>
                <Mono className="text-caption text-fg-secondary break-all">{repo}</Mono>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-caption text-fg-muted">Lista repozytoriów przyjdzie z konfiguracji runnera.</p>
        )}
        {runner.token && (
          <p className="text-caption text-fg-muted">
            Token <Mono>{runner.token.prefix}…</Mono>
            {runner.token.status === 'revoked'
              ? ' · cofnięty'
              : runner.token.status === 'expired'
                ? ' · wygasł'
                : runner.token.expiresAt
                  ? ` · ważny do ${formatDate(runner.token.expiresAt)}`
                  : ''}
          </p>
        )}
      </div>
      {canEdit && (
        <Menu>
          <MenuTrigger asChild>
            <IconButton icon={MoreHorizontal} label={`Akcje: ${runner.name}`} size={32} className="max-md:size-11" />
          </MenuTrigger>
          <MenuContent align="end">
            <MenuItem onSelect={() => setRenaming(true)}>Zmień nazwę</MenuItem>
            <MenuItem onSelect={() => rotate.mutate({ id: runner.id })}>Nowy token</MenuItem>
            {runner.status === 'disabled' ? (
              <MenuItem onSelect={() => update.mutate({ id: runner.id, disabled: false })}>Włącz</MenuItem>
            ) : (
              <MenuItem onSelect={() => update.mutate({ id: runner.id, disabled: true })}>Wyłącz</MenuItem>
            )}
          </MenuContent>
        </Menu>
      )}
      <RenameRunnerDialog runner={runner} open={renaming} onOpenChange={setRenaming} />
    </li>
  );
}

function RenameRunnerDialog({
  runner,
  open,
  onOpenChange,
}: {
  runner: RunnerDto;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const utils = trpc.useUtils();
  const [name, setName] = useState(runner.name);
  useEffect(() => {
    if (open) setName(runner.name);
  }, [open, runner.name]);
  const update = trpc.agents.updateRunner.useMutation({
    onSuccess: async () => {
      await utils.agents.invalidate();
      onOpenChange(false);
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      width="s"
      title="Zmień nazwę komputera"
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className={TOUCH_BUTTON}>
            Anuluj
          </Button>
          <Button
            variant="primary"
            loading={update.isPending}
            disabled={!name.trim()}
            onClick={() => update.mutate({ id: runner.id, name: name.trim() })}
            className={TOUCH_BUTTON}
          >
            Zapisz
          </Button>
        </>
      }
    >
      <Field label="Nazwa">
        <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={80} />
      </Field>
    </Dialog>
  );
}

const EXPIRY_OPTIONS = [
  { value: '365', label: '1 rok' },
  { value: '90', label: '90 dni' },
  { value: '30', label: '30 dni' },
];

export function AddRunnerDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (created: RunnerCreated) => void;
}) {
  const [name, setName] = useState('');
  const [repos, setRepos] = useState('');
  const [expiry, setExpiry] = useState('365');
  const utils = trpc.useUtils();
  const [errors, setErrors] = useState<{ name?: string; repos?: string; form?: string }>({});
  useEffect(() => {
    if (!open) return;
    setName('');
    setRepos('');
    setExpiry('365');
    setErrors({});
  }, [open]);
  const create = trpc.agents.createRunner.useMutation({
    onSuccess: async (c) => {
      await utils.agents.listRunners.invalidate();
      onCreated(c);
    },
    onError: (err) => {
      const issues = validationIssues(err);
      setErrors({
        name: issues.find((i) => i.path.startsWith('name'))?.message,
        repos: issues.find((i) => i.path.startsWith('repoAllowlist'))?.message,
        form: issues.length === 0 ? errorMessage(err) : undefined,
      });
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const list = repos
      .split(/\r?\n/)
      .map((l) => l.trim())
      .filter(Boolean);
    const bad = list.find((r) => normalizeRepoUrl(r) === null);
    if (!name.trim()) return setErrors({ name: 'Podaj nazwę komputera' });
    if (bad) return setErrors({ repos: `Nieprawidłowy adres repozytorium: ${bad}` });
    setErrors({});
    create.mutate({ name: name.trim(), repoAllowlist: list, expiresInDays: Number(expiry) });
  };
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      width="l"
      title="Dodaj komputer"
      description="Komputer z runnerem wykonuje Twoje zlecenia AI. Token zobaczysz tylko raz."
    >
      <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
        <Field label="Nazwa" error={errors.name}>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={80}
            placeholder="Laptop Sebastiana"
          />
        </Field>
        <Field
          label="Repozytoria"
          hint="Jeden adres w wierszu, np. https://github.com/fravity/rea-www. Runner może je też zgłosić sam."
          error={errors.repos}
        >
          <Textarea value={repos} onChange={(e) => setRepos(e.target.value)} rows={3} />
        </Field>
        <Field label="Ważność tokenu">
          <Select value={expiry} onValueChange={setExpiry} options={EXPIRY_OPTIONS} aria-label="Ważność tokenu" />
        </Field>
        {errors.form && <FormMessage>{errors.form}</FormMessage>}
        <div className="flex flex-wrap items-center justify-end gap-2 pt-1">
          <Button variant="ghost" onClick={() => onOpenChange(false)} className={TOUCH_BUTTON}>
            Anuluj
          </Button>
          <Button variant="primary" type="submit" loading={create.isPending} className={TOUCH_BUTTON}>
            Utwórz token
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

/** Token runnera pokazany raz + instrukcja uruchomienia (RUNNER_PROTOCOL §6). */
export function RunnerTokenDialog({ created, onClose }: { created: RunnerCreated | null; onClose: () => void }) {
  const steps = runnerSetupSteps(serverUrl());
  const env = created ? `setx FOS_RUNNER_TOKEN "${created.secret}"` : '';
  return (
    <Dialog
      open={created !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      width="l"
      title={created ? `Token dla „${created.runner.name}”` : 'Token runnera'}
    >
      {created && (
        <div className="flex flex-col gap-5">
          <ContextBanner tone="warning">{TOKEN_MESSAGES.showOnce}</ContextBanner>
          <div className="flex flex-col gap-2">
            <Label>Token</Label>
            <div className="flex items-center gap-2">
              <Mono
                aria-label="Token runnera"
                className="min-w-0 flex-1 rounded-lg bg-surface px-3 py-2 text-caption break-all text-fg select-all"
              >
                {created.secret}
              </Mono>
              <Button
                variant="secondary"
                icon={Copy}
                aria-label="Kopiuj token"
                onClick={() => void copyText(created.secret, 'Token skopiowany')}
                className={TOUCH_ICON}
              />
            </div>
            <p className="text-caption text-fg-muted">
              Windows: <Mono className="break-all">{env.replace(created.secret, '…')}</Mono>
            </p>
          </div>
          <ol className="flex flex-col gap-3">
            {steps.map((step, i) => (
              <li key={step.title} className="flex gap-3">
                <Mono className="mt-0.5 text-caption text-fg-muted">{i + 1}.</Mono>
                <div className="min-w-0">
                  <p className="text-body-s font-medium text-fg">{step.title}</p>
                  <p className="text-caption break-words text-fg-secondary">{step.detail}</p>
                </div>
              </li>
            ))}
          </ol>
          <div className="flex justify-end pt-1">
            <Button variant="primary" onClick={onClose} className={TOUCH_BUTTON}>
              Gotowe
            </Button>
          </div>
        </div>
      )}
    </Dialog>
  );
}
